import type { AgentAttachment } from '@commenda/types';
import { createContext, useContext } from 'react';
import type { QuestionAsk } from './question-block';

/** The question awaiting an answer. Cards dock above the composer rather than rendering in the
 * stream, so a long option list never scrolls its own question out of view. */
export interface CommendaAiLiveCard {
  toolCallId: string;
  ask: QuestionAsk;
  onDismiss: () => void;
}

/** One question's answer: every option the user chose, by the question's position in the card. */
export interface CommendaAiQuestionAnswer {
  questionIndex: number;
  chosen: string[];
}

/** Chat-surface actions for components rendered inside a message.
 *
 * Exposes the conversation's own submit path, so a card answer behaves exactly like a composer
 * send: page context harvested, files attached, view scrolled. */
export interface CommendaAiActions {
  submit: (text: string, attachments: AgentAttachment[]) => void;
  /** Sends an answer and records it locally in the same step. The local copy is what collapses the
   * card immediately: the answer is stored server-side on the asking message, and the chat loads
   * history once at mount, so nothing would otherwise tell the open page it had been answered. */
  submitQuestionAnswer: (payload: {
    toolCallId: string;
    answers: CommendaAiQuestionAnswer[];
    text: string;
    attachments?: AgentAttachment[];
  }) => void;
  /** Answers given in this session, by the call that asked. A key is present once the question was
   * answered, even by a bare Skip (an empty list). Empty on a freshly loaded page. */
  answeredInSession: Record<string, CommendaAiQuestionAnswer[]>;
  isGenerating: boolean;
  /** Joins the queue for the docked slot. The first claimant is the one shown; the next takes the
   * slot when the first clears. Re-claiming an id already queued keeps its place. */
  setLiveCard: (card: CommendaAiLiveCard) => void;
  clearLiveCard: (toolCallId: string) => void;
  /** Re-reads the conversation and appends assistant messages the backend wrote while it was open,
   * such as a document reader finish message. Resolves with how many it appended; rejects when the
   * read fails. */
  showNewAssistantMessages: () => Promise<number>;
}

export const CommendaAiActionsContext = createContext<CommendaAiActions | null>(
  null
);

export function useCommendaAiActions(): CommendaAiActions | null {
  return useContext(CommendaAiActionsContext);
}
